/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../src/components/ui/card.jsx";
import { Button } from "../../src/components/ui/button.jsx";
import { Copy, Check, Download } from "lucide-react";
import { Badge } from "../../src/components/ui/badge.jsx";

export default function JsonPreview({ payload }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `test-config-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getValueByPath = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  };

  const REQUIRED_FIELDS_BY_STATE = {
    qa: [
      'org_id',
      'state',
      'project.council_name',
      'project.address',
    ],
    planning_pathway: [
      'org_id',
      'state',
      'project.council_name',
      'project.address',
      'project.planning_pathway',
    ],
    document_checklist: [
      'org_id',
      'state',
      'project.council_name',
      'project.address',
      'project.zone',
    ],
    application: [
      'org_id',
      'state',
      'project.council_name',
      'project.address',
      'project.zone',
      'project.active_precinct',
    ],
  };

  const { completion, summary } = useMemo(() => {
    const config = payload?.user_config || {};
    const required = [
      'org_id',
      'state',
      'project.council_name',
      'project.address',
      'project.active_precinct',
      'project_id',
      'conversation_id',
      'user_id',
    ];

    // Count config completeness
    let filled = required.reduce((count, path) => {
      const val = getValueByPath(config, path);
      const ok = typeof val === 'string' ? (val.trim() ? 1 : 0) : (val ? 1 : 0);
      return count + ok;
    }, 0);

    // Additional required items for 100% completion: title, chat_input, and one of expected_answer or expected_question
    const titleFilled = typeof payload?.title === 'string' && payload.title.trim() !== '';
    const chatInputFilled = typeof payload?.chat_input === 'string' && payload.chat_input.trim() !== '';
    const expectedAnswerPresent = typeof payload?.expected_answer === 'string' && payload.expected_answer.trim() !== '';
    const expectedQuestionPresent = typeof payload?.expected_question === 'string' && payload.expected_question.trim() !== '';
    const hasOneExpected = expectedAnswerPresent || expectedQuestionPresent;

    const extraTotal = 3;
    const extraFilled = (titleFilled ? 1 : 0) + (chatInputFilled ? 1 : 0) + (hasOneExpected ? 1 : 0);

    const total = required.length + extraTotal;
    filled += extraFilled;

    const authStatus = payload?.meta?.auth_status;
    const loggedIn = authStatus === 'signed_in';
    const address = config?.project?.address || '(no address)';
    const stateLabel = config.state || '(no state)';
    const summary = { loggedIn, stateLabel, address };
    return { completion: { filled, total }, summary };
  }, [payload]);

  const completionPercentage = completion.total > 0 ? Math.round((completion.filled / completion.total) * 100) : 0;

  return (
    <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-sm sticky top-4">
      <CardHeader className="border-b border-slate-100">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg font-bold text-slate-800 mb-2">JSON Preview</CardTitle>
            <Badge variant="outline" className={completionPercentage > 50 ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}>
              {completionPercentage}% Complete
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className="flex items-center gap-2"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied!' : 'Copy'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="flex items-center gap-2"
            >
              <Download className="w-3 h-3" />
              Download
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-100 text-sm flex flex-col gap-1">
          <div>
            <span className="text-slate-600">Status:</span> {summary.loggedIn ? <span className="text-emerald-700 font-medium">Logged in</span> : <span className="text-slate-700 font-medium">Anonymous</span>}
          </div>
          <div>
            <span className="text-slate-600">State:</span> <span className="text-slate-800">{summary.stateLabel}</span>
          </div>
          <div className="truncate">
            <span className="text-slate-600">Address:</span> <span className="text-slate-800">{summary.address}</span>
          </div>
        </div>
        <div className="relative">
          <pre className="text-xs bg-slate-900 text-slate-300 p-4 rounded-b-lg overflow-auto max-h-96 font-mono leading-relaxed">
            {JSON.stringify(payload, null, 2)}
          </pre>
        </div>
      </CardContent>
    </Card>
  );
}